import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import partyService from '../../services/partyService';
import masterService from '../../services/masterService';
import { b2bService } from '../../services/b2bService';
import MasterPageWrapper from './common/MasterPageWrapper';
import MasterToolbar from './components/MasterToolbar';
import MasterTable from './components/MasterTable';
import StatusBadge from './components/StatusBadge';
import ActionMenu from './components/ActionMenu';
import ConfirmModal from './components/ConfirmModal';
import usePagination from '../../hooks/usePagination';
import './PartyMasterPage.css';

const INITIAL_PARTY = {
  partyId: 0,
  partyCode: '',
  partyName: '',
  classificationId: '',
  defaultPanelId: '',
  contactPerson: '',
  contactMobile: '',
  contactEmail: '',
  address: '',
  city: '',
  state: '',
  gstNumber: '',
  panNumber: '',
  creditLimit: 0.00,
  paymentTermsDays: 0,
  creditPolicy: 'HYBRID',
  breachAction: 'BLOCK_REGISTRATION',
  gracePeriodDays: 7,
  isActive: true,
  // Client Portal Credentials
  enablePortalAccess: false,
  portalUsername: '',
  portalPassword: '',
  portalRole: 'ADMIN'
};

export const PartyMasterPage = ({ showToast: propShowToast }) => {
  const navigate = useNavigate();

  // Stable Toast reference to prevent re-render / callback loops
  const showToastRef = useRef(propShowToast || (() => {}));
  useEffect(() => {
    showToastRef.current = propShowToast || (() => {});
  }, [propShowToast]);

  const triggerToast = useCallback((msg, type = 'info') => {
    if (typeof showToastRef.current === 'function') {
      showToastRef.current(msg, type);
    }
  }, []);

  const [viewMode, setViewMode] = useState('list');
  const [isEditMode, setIsEditMode] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form State
  const [formData, setFormData] = useState(INITIAL_PARTY);
  const [errors, setErrors] = useState({});
  const [showPortalPassword, setShowPortalPassword] = useState(false);

  // Lookups
  const [classifications, setClassifications] = useState([]);
  const [panels, setPanels] = useState([]);

  // List State
  const [parties, setParties] = useState([]);
  const [loadingParties, setLoadingParties] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [classFilter, setClassFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Quick Portal Credential Modal State
  const [credModalTarget, setCredModalTarget] = useState(null);
  const [modalUsername, setModalUsername] = useState('');
  const [modalPassword, setModalPassword] = useState('');
  const [modalRole, setModalRole] = useState('ADMIN');
  const [modalShowPassword, setModalShowPassword] = useState(false);
  const [savingCreds, setSavingCreds] = useState(false);

  // Financial Ledger (Khata) Modal State
  const [ledgerModalTarget, setLedgerModalTarget] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerData, setLedgerData] = useState(null);
  const [ledgerFromDate, setLedgerFromDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 60);
    return d.toISOString().split('T')[0];
  });
  const [ledgerToDate, setLedgerToDate] = useState(() => new Date().toISOString().split('T')[0]);

  // Advance Deposit / Wallet Recharge Modal State
  const [advanceModalTarget, setAdvanceModalTarget] = useState(null);
  const [advanceAmount, setAdvanceAmount] = useState(20000);
  const [advanceMode, setAdvanceMode] = useState('UPI');
  const [advanceRef, setAdvanceRef] = useState('');
  const [advanceBank, setAdvanceBank] = useState('');
  const [advanceNotes, setAdvanceNotes] = useState('Advance deposit for quota recharge');
  const [savingAdvance, setSavingAdvance] = useState(false);

  // Centralized Pagination
  const { paginatedItems: pagedParties, paginationProps: partyPaginationProps } = usePagination({
    items: parties,
    resetDeps: [searchQuery, classFilter, statusFilter]
  });

  // Confirm Modal
  const [confirmTarget, setConfirmTarget] = useState(null);

  // Load Lookups
  const loadLookups = useCallback(async () => {
    try {
      const [classRes, panelsRes] = await Promise.all([
        partyService.getClassifications({ isActive: true }),
        masterService.getPanels({ isActive: true })
      ]);

      if (classRes && classRes.success) {
        setClassifications(classRes.data || []);
      }
      if (panelsRes && panelsRes.success) {
        setPanels(panelsRes.data || []);
      }
    } catch (err) {
      console.error('Error loading party lookups:', err);
    }
  }, []);

  // Load Parties — safe without loop
  const loadParties = useCallback(async () => {
    setLoadingParties(true);
    try {
      const params = {};
      if (statusFilter !== 'all') {
        params.isActive = statusFilter === 'active';
      }
      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
      }
      if (classFilter) {
        params.classificationId = parseInt(classFilter, 10);
      }

      const res = await partyService.getParties(params);
      if (res && res.success) {
        setParties(res.data || []);
      }
    } catch (err) {
      console.error('Error loading parties:', err);
      triggerToast(err.response?.data?.message || 'Failed to load parties', 'error');
    } finally {
      setLoadingParties(false);
    }
  }, [searchQuery, classFilter, statusFilter, triggerToast]);

  useEffect(() => {
    loadLookups();
  }, [loadLookups]);

  useEffect(() => {
    if (viewMode === 'list') {
      loadParties();
    }
  }, [viewMode, loadParties]);

  // Add / Edit Handlers
  const handleAddNew = () => {
    setFormData(INITIAL_PARTY);
    setErrors({});
    setIsEditMode(false);
    setShowPortalPassword(false);
    setViewMode('form');
  };

  const handleEdit = (party) => {
    setIsEditMode(true);
    setShowPortalPassword(false);
    setFormData({
      partyId: party.partyId,
      partyCode: party.partyCode || '',
      partyName: party.partyName || '',
      classificationId: party.classificationId || '',
      defaultPanelId: party.defaultPanelId || '',
      contactPerson: party.contactPerson || '',
      contactMobile: party.contactMobile || '',
      contactEmail: party.contactEmail || '',
      address: party.address || '',
      city: party.city || '',
      state: party.state || '',
      gstNumber: party.gstNumber || '',
      panNumber: party.panNumber || '',
      creditLimit: party.creditLimit || 0,
      paymentTermsDays: party.paymentTermsDays || 0,
      creditPolicy: party.creditPolicy || 'HYBRID',
      breachAction: party.breachAction || 'BLOCK_REGISTRATION',
      gracePeriodDays: party.gracePeriodDays ?? 7,
      isActive: party.isActive,
      // Portal properties
      enablePortalAccess: party.hasPortalAccess || Boolean(party.portalUsername),
      portalUsername: party.portalUsername || party.partyCode?.toLowerCase() || '',
      portalPassword: '',
      portalRole: party.portalRole || 'ADMIN'
    });
    setErrors({});
    setViewMode('form');
  };

  // Financial Ledger (Khata) Handler
  const handleOpenLedgerModal = async (party) => {
    setLedgerModalTarget(party);
    setLedgerLoading(true);
    setLedgerData(null);
    try {
      const res = await partyService.getPartyLedger(party.partyId, {
        fromDate: ledgerFromDate,
        toDate: ledgerToDate
      });
      if (res && res.success) {
        setLedgerData(res.data);
      } else {
        triggerToast('Could not load ledger statement', 'warning');
      }
    } catch (err) {
      console.error('Error fetching party ledger:', err);
      triggerToast(err.response?.data?.message || 'Error fetching client ledger', 'error');
    } finally {
      setLedgerLoading(false);
    }
  };

  const handleFilterLedger = async () => {
    if (!ledgerModalTarget) return;
    setLedgerLoading(true);
    try {
      const res = await partyService.getPartyLedger(ledgerModalTarget.partyId, {
        fromDate: ledgerFromDate,
        toDate: ledgerToDate
      });
      if (res && res.success) {
        setLedgerData(res.data);
      }
    } catch (err) {
      triggerToast('Error refreshing ledger statement', 'error');
    } finally {
      setLedgerLoading(false);
    }
  };

  // Advance Deposit / Wallet Recharge Handlers
  const handleOpenAdvanceModal = (party) => {
    setAdvanceModalTarget(party);
    setAdvanceAmount(20000);
    setAdvanceMode('UPI');
    setAdvanceRef('');
    setAdvanceBank('');
    setAdvanceNotes('Advance deposit for test quota recharge');
  };

  const handleCreateAdvance = async (e) => {
    e.preventDefault();
    if (!advanceModalTarget) return;
    const numAmount = parseFloat(advanceAmount);
    if (isNaN(numAmount) || numAmount <= 0) {
      triggerToast('Please enter a valid deposit amount greater than ₹0', 'warning');
      return;
    }

    setSavingAdvance(true);
    try {
      const payload = {
        partyId: advanceModalTarget.partyId,
        amount: numAmount,
        paymentMode: advanceMode,
        transactionReference: advanceRef.trim() || undefined,
        bankName: advanceBank.trim() || undefined,
        notes: advanceNotes.trim() || undefined
      };
      const res = await b2bService.createAdvance(payload);
      const receiptNo = res?.receiptNumber || res?.ReceiptNumber || 'ADVANCE';
      triggerToast(`Advance ${receiptNo} for ₹${numAmount.toLocaleString('en-IN')} credited to ${advanceModalTarget.partyName}!`, 'success');
      setAdvanceModalTarget(null);

      // Refresh Ledger if currently viewed
      if (ledgerModalTarget && (ledgerModalTarget.partyId === advanceModalTarget.partyId)) {
        try {
          const lRes = await partyService.getPartyLedger(ledgerModalTarget.partyId, {
            fromDate: ledgerFromDate,
            toDate: ledgerToDate
          });
          if (lRes && lRes.success) setLedgerData(lRes.data);
        } catch (_) {}
      }

      loadParties();
    } catch (err) {
      console.error('Error creating advance:', err);
      triggerToast(err.response?.data?.message || err.message || 'Failed to record advance deposit', 'error');
    } finally {
      setSavingAdvance(false);
    }
  };

  // Status Toggle
  const handleToggleStatus = (party) => {
    setConfirmTarget({
      partyId: party.partyId,
      partyName: party.partyName,
      nextStatus: !party.isActive
    });
  };

  const confirmStatusChange = async () => {
    if (!confirmTarget) return;
    try {
      await partyService.updatePartyStatus(confirmTarget.partyId, confirmTarget.nextStatus);
      triggerToast(`Party ${confirmTarget.nextStatus ? 'activated' : 'deactivated'} successfully.`, 'success');
      setConfirmTarget(null);
      loadParties();
    } catch (err) {
      triggerToast(err.response?.data?.message || 'Failed to update status', 'error');
    }
  };

  // Validation
  const validateForm = () => {
    const errs = {};
    if (!formData.partyCode.trim()) errs.partyCode = 'Party code is required';
    if (!formData.partyName.trim()) errs.partyName = 'Party / Client name is required';
    if (!formData.classificationId) errs.classificationId = 'Please select a classification';

    if (formData.enablePortalAccess) {
      if (!formData.portalUsername.trim()) {
        errs.portalUsername = 'Portal Username is required when client portal is enabled';
      }
      if (!isEditMode && !formData.portalPassword) {
        errs.portalPassword = 'An initial portal password is required';
      }
      if (formData.portalPassword && formData.portalPassword.length < 6) {
        errs.portalPassword = 'Password must be at least 6 characters';
      }
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // Save Party
  const handleSave = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      triggerToast('Please correct the highlighted fields.', 'warning');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        partyCode: formData.partyCode.trim().toUpperCase(),
        partyName: formData.partyName.trim(),
        classificationId: parseInt(formData.classificationId, 10),
        defaultPanelId: formData.defaultPanelId ? parseInt(formData.defaultPanelId, 10) : null,
        contactPerson: formData.contactPerson?.trim() || null,
        contactMobile: formData.contactMobile?.trim() || null,
        contactEmail: formData.contactEmail?.trim() || null,
        address: formData.address?.trim() || null,
        city: formData.city?.trim() || null,
        state: formData.state?.trim() || null,
        gstNumber: formData.gstNumber?.trim() || null,
        panNumber: formData.panNumber?.trim() || null,
        creditLimit: parseFloat(formData.creditLimit) || 0.00,
        paymentTermsDays: parseInt(formData.paymentTermsDays, 10) || 0,
        creditPolicy: formData.creditPolicy || 'HYBRID',
        breachAction: formData.breachAction || 'BLOCK_REGISTRATION',
        gracePeriodDays: parseInt(formData.gracePeriodDays, 10) || 7,
        // Portal fields
        enablePortalAccess: formData.enablePortalAccess,
        portalUsername: formData.enablePortalAccess ? formData.portalUsername.trim() : null,
        portalPassword: formData.enablePortalAccess && formData.portalPassword ? formData.portalPassword : null,
        portalRole: formData.enablePortalAccess ? formData.portalRole : 'ADMIN'
      };

      if (isEditMode) {
        await partyService.updateParty(formData.partyId, {
          ...payload,
          isActive: formData.isActive
        });
        triggerToast('B2B Client Party and Portal credentials updated successfully.', 'success');
      } else {
        await partyService.createParty(payload);
        triggerToast('B2B Client Party created with Portal Access credentials.', 'success');
      }

      setViewMode('list');
      loadParties();
    } catch (err) {
      console.error('Error saving party:', err);
      triggerToast(err.response?.data?.message || 'Error saving party', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Open Quick Credential Modal from Table Action
  const handleOpenCredModal = (party) => {
    setCredModalTarget(party);
    setModalUsername(party.portalUsername || party.partyCode?.toLowerCase() || '');
    setModalPassword('');
    setModalRole(party.portalRole || 'ADMIN');
    setModalShowPassword(false);
  };

  const handleSaveModalCreds = async () => {
    if (!credModalTarget) return;
    if (!modalUsername.trim()) {
      triggerToast('Portal username is required.', 'warning');
      return;
    }
    if (!modalPassword.trim() && !credModalTarget.hasPortalAccess) {
      triggerToast('Password is required for new portal account.', 'warning');
      return;
    }

    setSavingCreds(true);
    try {
      await partyService.provisionPortalUser(credModalTarget.partyId, {
        username: modalUsername.trim(),
        password: modalPassword.trim() || null,
        role: modalRole,
        fullName: credModalTarget.contactPerson || credModalTarget.partyName,
        email: credModalTarget.contactEmail || `${modalUsername.trim()}@client.lims`,
        mobile: credModalTarget.contactMobile,
        isActive: true
      });
      triggerToast(`Portal login credentials updated for ${credModalTarget.partyName}! Client can log in at /b2b/login.`, 'success');
      setCredModalTarget(null);
      loadParties();
    } catch (err) {
      triggerToast(err.response?.data?.message || 'Failed to update portal credentials', 'error');
    } finally {
      setSavingCreds(false);
    }
  };

  // Table Columns
  const columns = [
    {
      key: 'partyCode',
      header: 'Party Code',
      render: (row) => <strong style={{ color: '#2563eb', fontFamily: 'monospace', fontSize: '0.9rem' }}>{row.partyCode}</strong>
    },
    {
      key: 'partyName',
      header: 'Business Client / Party Name',
      render: (row) => (
        <div>
          <div style={{ fontWeight: 600, color: '#0f172a' }}>{row.partyName}</div>
          {row.contactPerson && (
            <div style={{ fontSize: '0.78rem', color: '#64748b' }}>
              👤 {row.contactPerson} {row.contactMobile ? `(${row.contactMobile})` : ''}
            </div>
          )}
        </div>
      )
    },
    {
      key: 'classificationName',
      header: 'Classification',
      render: (row) => (
        <span style={{
          background: '#f1f5f9',
          color: '#334155',
          padding: '0.2rem 0.6rem',
          borderRadius: '4px',
          fontSize: '0.75rem',
          fontWeight: 600
        }}>
          {row.classificationName}
        </span>
      )
    },
    {
      key: 'creditPolicy',
      header: 'Credit Policy & Terms',
      render: (row) => {
        const policy = row.creditPolicy || 'HYBRID';
        const limit = parseFloat(row.creditLimit || 0);
        const days = row.paymentTermsDays || 0;
        return (
          <div>
            {policy === 'HYBRID' && (
              <span className="badge-policy hybrid">⚡ Hybrid • ₹{limit.toLocaleString('en-IN')} / {days}d</span>
            )}
            {policy === 'PREPAID' && (
              <span className="badge-policy prepaid">💳 Prepaid Advance Only</span>
            )}
            {policy === 'LIMIT_ONLY' && (
              <span className="badge-policy limit">💰 Limit: ₹{limit.toLocaleString('en-IN')}</span>
            )}
            {policy === 'DAYS_ONLY' && (
              <span className="badge-policy days">📅 {days} Days Credit Term</span>
            )}
            <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '2px' }}>
              Breach: <span style={{ fontWeight: 600, color: '#334155' }}>{(row.breachAction || 'BLOCK_REGISTRATION').replace('BLOCK_', 'HOLD ')}</span>
            </div>
          </div>
        );
      }
    },
    {
      key: 'totalExposure',
      header: 'Live Exposure / Headroom',
      render: (row) => {
        const exposure = parseFloat(row.totalExposure || 0);
        const available = parseFloat(row.availableCredit || 0);
        return (
          <div>
            <div style={{ fontWeight: 600, color: exposure > 0 ? '#b45309' : '#0f172a' }}>
              Exposure: ₹{exposure.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </div>
            <div style={{ fontSize: '0.75rem', color: available > 0 ? '#16a34a' : '#dc2626', fontWeight: 600 }}>
              Headroom: ₹{available.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </div>
          </div>
        );
      }
    },
    {
      key: 'portalAccess',
      header: 'Client Portal Login',
      render: (row) => (
        row.hasPortalAccess ? (
          <span className="badge-portal-active" title={`Username: ${row.portalUsername} (${row.portalRole})`}>
            🔑 {row.portalUsername} <span style={{ opacity: 0.7 }}>({row.portalRole})</span>
          </span>
        ) : (
          <span className="badge-portal-none">Not Configured</span>
        )
      )
    },
    {
      key: 'isActive',
      header: 'Status',
      render: (row) => <StatusBadge isActive={row.isActive} />
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (row) => (
        <ActionMenu
          actions={[
            {
              label: '💳 Recharge Wallet / Add Advance',
              onClick: () => handleOpenAdvanceModal(row)
            },
            {
              label: '📖 View Financial Ledger (Khata)',
              onClick: () => handleOpenLedgerModal(row)
            },
            {
              label: '🔑 Manage Portal Login',
              onClick: () => handleOpenCredModal(row)
            },
            {
              label: '📋 View / Manage Contracts',
              onClick: () => navigate(`/masters/contracts?partyId=${row.partyId}`)
            },
            {
              label: '✏️ Edit Party Details',
              onClick: () => handleEdit(row)
            },
            {
              label: row.isActive ? 'Deactivate' : 'Activate',
              onClick: () => handleToggleStatus(row),
              danger: row.isActive
            }
          ]}
        />
      )
    }
  ];

  return (
    <MasterPageWrapper
      masterTitle="B2B Client & Party Master"
      masterName="Party"
      viewMode={viewMode}
      onToggleView={(mode) => {
        if (mode === 'form') handleAddNew();
        else setViewMode('list');
      }}
    >
      {viewMode === 'list' ? (
        <div className="party-master-container">
          <MasterToolbar
            searchPlaceholder="Search by Party Name, Code, Contact..."
            searchValue={searchQuery}
            onSearchChange={setSearchQuery}
            filters={[
              {
                id: 'classFilter',
                label: 'Classification',
                value: classFilter,
                onChange: setClassFilter,
                options: [
                  { value: '', label: 'All Classifications' },
                  ...classifications.map(c => ({ value: String(c.classificationId), label: c.name }))
                ]
              },
              {
                id: 'statusFilter',
                label: 'Status',
                value: statusFilter,
                onChange: setStatusFilter,
                options: [
                  { value: 'all', label: 'All Statuses' },
                  { value: 'active', label: 'Active Only' },
                  { value: 'inactive', label: 'Inactive Only' }
                ]
              }
            ]}
          />

          <MasterTable
            columns={columns}
            data={pagedParties}
            loading={loadingParties}
            emptyTitle="No B2B Parties Found"
            emptyDescription="Register your corporate clients, hospital tie-ups, clinics, and collection centers."
            pagination={partyPaginationProps}
          />
        </div>
      ) : (
        <form onSubmit={handleSave} className="party-master-container">
          {/* Card 1: Business Identification */}
          <div className="party-form-card">
            <div className="party-section-header">
              <div className="party-section-title-wrap">
                <div className="party-section-icon blue">🏢</div>
                <div>
                  <h3 className="party-section-title">Business Identification</h3>
                  <p className="party-section-desc">Specify unique business code, legal name, and commercial classification.</p>
                </div>
              </div>
            </div>

            <div className="party-grid-3">
              <div className="party-field-group">
                <label className="party-field-label">Party Code <span className="req">*</span></label>
                <input
                  type="text"
                  className={`party-field-input ${errors.partyCode ? 'has-error' : ''}`}
                  placeholder="e.g. CORP_INFOSYS, HOSP_APOLLO"
                  value={formData.partyCode}
                  onChange={(e) => setFormData({ ...formData, partyCode: e.target.value.toUpperCase() })}
                  disabled={isEditMode}
                />
                {errors.partyCode && <span className="party-field-error">{errors.partyCode}</span>}
                <span className="party-field-hint">Unique institutional identifier</span>
              </div>

              <div className="party-field-group" style={{ gridColumn: 'span 2' }}>
                <label className="party-field-label">Client / Business Name <span className="req">*</span></label>
                <input
                  type="text"
                  className={`party-field-input ${errors.partyName ? 'has-error' : ''}`}
                  placeholder="e.g. Infosys Technologies Ltd (Health Division)"
                  value={formData.partyName}
                  onChange={(e) => setFormData({ ...formData, partyName: e.target.value })}
                />
                {errors.partyName && <span className="party-field-error">{errors.partyName}</span>}
              </div>
            </div>

            <div className="party-grid-2">
              <div className="party-field-group">
                <label className="party-field-label">Party Classification <span className="req">*</span></label>
                <select
                  className={`party-field-input ${errors.classificationId ? 'has-error' : ''}`}
                  value={formData.classificationId}
                  onChange={(e) => setFormData({ ...formData, classificationId: e.target.value })}
                >
                  <option value="">-- Select Classification --</option>
                  {classifications.map(c => (
                    <option key={c.classificationId} value={c.classificationId}>
                      {c.name}
                    </option>
                  ))}
                </select>
                {errors.classificationId && <span className="party-field-error">{errors.classificationId}</span>}
              </div>

              <div className="party-field-group">
                <label className="party-field-label">Default Panel Tariff</label>
                <select
                  className="party-field-input"
                  value={formData.defaultPanelId}
                  onChange={(e) => setFormData({ ...formData, defaultPanelId: e.target.value })}
                >
                  <option value="">-- Standard Cash / Walk-in Tariff --</option>
                  {panels.map(p => (
                    <option key={p.panelId} value={p.panelId}>
                      {p.panelName} ({p.panelCode})
                    </option>
                  ))}
                </select>
                <span className="party-field-hint">Fallback tariff if contract rate is not mapped</span>
              </div>
            </div>

            <div className="party-grid-2">
              <div className="party-field-group">
                <label className="party-field-label">GSTIN / Tax ID</label>
                <input
                  type="text"
                  className="party-field-input"
                  placeholder="e.g. 27AAAAA0000A1Z5"
                  value={formData.gstNumber}
                  onChange={(e) => setFormData({ ...formData, gstNumber: e.target.value.toUpperCase() })}
                />
              </div>

              <div className="party-field-group">
                <label className="party-field-label">PAN Number</label>
                <input
                  type="text"
                  className="party-field-input"
                  placeholder="e.g. ABCDE1234F"
                  value={formData.panNumber}
                  onChange={(e) => setFormData({ ...formData, panNumber: e.target.value.toUpperCase() })}
                />
              </div>
            </div>
          </div>

          {/* Card 2: Commercial Credit & Risk Governance */}
          <div className="party-form-card">
            <div className="party-section-header">
              <div className="party-section-title-wrap">
                <div className="party-section-icon purple">🛡️</div>
                <div>
                  <h3 className="party-section-title">Commercial Credit &amp; Risk Governance</h3>
                  <p className="party-section-desc">Configure credit control model (limit vs days), grace periods, and automated breach enforcement.</p>
                </div>
              </div>
            </div>

            <div className="party-grid-3">
              <div className="party-field-group">
                <label className="party-field-label">Credit Control Policy <span className="req">*</span></label>
                <select
                  className="party-field-input"
                  value={formData.creditPolicy}
                  onChange={(e) => setFormData({ ...formData, creditPolicy: e.target.value })}
                >
                  <option value="HYBRID">⚡ Both: Credit Limit + Credit Days (Standard)</option>
                  <option value="LIMIT_ONLY">💰 Credit Limit (₹) Ceiling Only</option>
                  <option value="DAYS_ONLY">📅 Credit Terms (Days) Period Only</option>
                  <option value="PREPAID">💳 Prepaid Deposit / Advance Wallet Only</option>
                </select>
                <span className="party-field-hint">Operational policy governing account exposure</span>
              </div>

              <div className="party-field-group">
                <label className="party-field-label">Approved Credit Limit (₹)</label>
                <input
                  type="number"
                  step="0.01"
                  className="party-field-input"
                  value={formData.creditLimit}
                  onChange={(e) => setFormData({ ...formData, creditLimit: e.target.value })}
                  disabled={formData.creditPolicy === 'PREPAID' || formData.creditPolicy === 'DAYS_ONLY'}
                />
                <span className="party-field-hint">
                  {formData.creditPolicy === 'PREPAID' ? 'Disabled: Requires advance deposit' : 'Maximum allowable unbilled balance'}
                </span>
              </div>

              <div className="party-field-group">
                <label className="party-field-label">Credit / Payment Terms (Days)</label>
                <input
                  type="number"
                  className="party-field-input"
                  value={formData.paymentTermsDays}
                  onChange={(e) => setFormData({ ...formData, paymentTermsDays: e.target.value })}
                  disabled={formData.creditPolicy === 'PREPAID' || formData.creditPolicy === 'LIMIT_ONLY'}
                />
                <span className="party-field-hint">
                  {formData.creditPolicy === 'PREPAID' ? 'Disabled: Cash / Advance only' : 'Credit period from invoice date'}
                </span>
              </div>
            </div>

            <div className="party-grid-2">
              <div className="party-field-group">
                <label className="party-field-label">Automated Action on Breach / Expiry <span className="req">*</span></label>
                <select
                  className="party-field-input"
                  value={formData.breachAction}
                  onChange={(e) => setFormData({ ...formData, breachAction: e.target.value })}
                >
                  <option value="BLOCK_REGISTRATION">🚫 Block New Patient Booking &amp; Sample Collection</option>
                  <option value="BLOCK_REPORTS">📑 Block Final Signed Report Release &amp; PDF Download</option>
                  <option value="BLOCK_ALL">🔒 Strict Hard Lock: Block Both Booking &amp; Reports</option>
                  <option value="WARN_ONLY">⚠️ Soft Warning Only: Flash Alert &amp; Email Notice</option>
                </select>
                <span className="party-field-hint">Automated enforcement action triggered when credit terms are breached</span>
              </div>

              <div className="party-field-group">
                <label className="party-field-label">Grace Period (Days)</label>
                <input
                  type="number"
                  className="party-field-input"
                  value={formData.gracePeriodDays}
                  onChange={(e) => setFormData({ ...formData, gracePeriodDays: e.target.value })}
                />
                <span className="party-field-hint">Additional grace window before strict enforcement applies</span>
              </div>
            </div>

            {/* Dynamic Governance Explainer */}
            <div style={{ marginTop: '1rem', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px 16px', fontSize: '0.82rem', color: '#475569', lineHeight: 1.5 }}>
              <strong style={{ color: '#0f172a' }}>Multi-Center Governance Rule: </strong>
              {formData.creditPolicy === 'HYBRID' && 'Account will trigger automated hold if either total exposure exceeds ₹' + parseFloat(formData.creditLimit || 0).toLocaleString('en-IN') + ' OR invoices remain unpaid beyond ' + (parseInt(formData.paymentTermsDays, 10) || 0) + ' days.'}
              {formData.creditPolicy === 'LIMIT_ONLY' && 'Account will trigger automated hold strictly when total exposure crosses ₹' + parseFloat(formData.creditLimit || 0).toLocaleString('en-IN') + ' (amount-capped).'}
              {formData.creditPolicy === 'DAYS_ONLY' && 'Account allows unlimited volume within ' + (parseInt(formData.paymentTermsDays, 10) || 0) + ' days, but triggers hold if any invoice ages beyond term + grace window.'}
              {formData.creditPolicy === 'PREPAID' && 'Zero credit extended. Patient tests are only accepted if unapplied advance deposit balance covers the test booking amount.'}
            </div>
          </div>

          {/* Card 2: Contact & Location */}
          <div className="party-form-card">
            <div className="party-section-header">
              <div className="party-section-title-wrap">
                <div className="party-section-icon amber">📍</div>
                <div>
                  <h3 className="party-section-title">Contact &amp; Location</h3>
                  <p className="party-section-desc">Provide primary representative details and official billing address.</p>
                </div>
              </div>
            </div>

            <div className="party-grid-3">
              <div className="party-field-group">
                <label className="party-field-label">Contact Person</label>
                <input
                  type="text"
                  className="party-field-input"
                  placeholder="e.g. Dr. Rajesh Kulkarni"
                  value={formData.contactPerson}
                  onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })}
                />
              </div>

              <div className="party-field-group">
                <label className="party-field-label">Phone / Mobile</label>
                <input
                  type="text"
                  className="party-field-input"
                  placeholder="e.g. 9876543210"
                  value={formData.contactMobile}
                  onChange={(e) => setFormData({ ...formData, contactMobile: e.target.value })}
                />
              </div>

              <div className="party-field-group">
                <label className="party-field-label">Email Address</label>
                <input
                  type="email"
                  className="party-field-input"
                  placeholder="e.g. billing@partnerhospital.com"
                  value={formData.contactEmail}
                  onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                />
              </div>
            </div>

            <div className="party-grid-3">
              <div className="party-field-group">
                <label className="party-field-label">Address Line</label>
                <input
                  type="text"
                  className="party-field-input"
                  placeholder="Premises, Street, Building"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                />
              </div>

              <div className="party-field-group">
                <label className="party-field-label">City</label>
                <input
                  type="text"
                  className="party-field-input"
                  placeholder="e.g. Aurangabad"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                />
              </div>

              <div className="party-field-group">
                <label className="party-field-label">State</label>
                <input
                  type="text"
                  className="party-field-input"
                  placeholder="e.g. Maharashtra"
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                />
              </div>
            </div>
          </div>

          {/* Card 3: B2B Client Portal Login Credentials (KEY FEATURE REQUESTED) */}
          <div className="party-form-card" style={{ borderColor: '#bfdbfe', background: '#f8fafc' }}>
            <div className="party-section-header">
              <div className="party-section-title-wrap">
                <div className="party-section-icon purple">🔐</div>
                <div>
                  <h3 className="party-section-title">Client Partner Portal Access &amp; Login Credentials</h3>
                  <p className="party-section-desc">
                    Configure institutional username and password so this client can securely sign in at <strong style={{ color: '#2563eb' }}>/b2b/login</strong>.
                  </p>
                </div>
              </div>
              <span className="badge-portal-active" style={{ fontSize: '0.8rem', padding: '0.3rem 0.75rem' }}>
                Self-Service Portal
              </span>
            </div>

            <div className={`party-portal-box ${formData.enablePortalAccess ? 'active' : ''}`}>
              <div
                className="party-portal-toggle-header"
                onClick={() => setFormData(prev => ({
                  ...prev,
                  enablePortalAccess: !prev.enablePortalAccess,
                  portalUsername: !prev.enablePortalAccess && !prev.portalUsername
                    ? (prev.partyCode ? prev.partyCode.toLowerCase() : '')
                    : prev.portalUsername
                }))}
              >
                <div className="party-portal-toggle-left">
                  <input
                    type="checkbox"
                    className="party-portal-checkbox"
                    checked={formData.enablePortalAccess}
                    onChange={(e) => setFormData({
                      ...formData,
                      enablePortalAccess: e.target.checked,
                      portalUsername: e.target.checked && !formData.portalUsername
                        ? (formData.partyCode ? formData.partyCode.toLowerCase() : '')
                        : formData.portalUsername
                    })}
                  />
                  <div>
                    <h4 className="party-portal-title">Enable B2B Client Portal Login</h4>
                    <p className="party-portal-subtitle">
                      Allows authorized corporate / hospital staff to view real-time patient reports, download corporate invoices, and view ledger balance.
                    </p>
                  </div>
                </div>
              </div>

              {formData.enablePortalAccess && (
                <div style={{ marginTop: '1.25rem' }}>
                  <div className="party-portal-notice">
                    <span className="party-portal-notice-icon">ℹ️</span>
                    <div>
                      <strong>Portal URL for Client:</strong> The representative will use URL <code>/b2b/login</code> with the username and password below.
                      Tenant isolation is strictly enforced — this client will NEVER see another corporate partner's orders or financial records.
                    </div>
                  </div>

                  <div className="party-grid-3">
                    <div className="party-field-group">
                      <label className="party-field-label">Portal Username <span className="req">*</span></label>
                      <input
                        type="text"
                        className={`party-field-input ${errors.portalUsername ? 'has-error' : ''}`}
                        placeholder="e.g. techcorp_admin, apollo_lab"
                        value={formData.portalUsername}
                        onChange={(e) => setFormData({ ...formData, portalUsername: e.target.value.toLowerCase().trim() })}
                      />
                      {errors.portalUsername && <span className="party-field-error">{errors.portalUsername}</span>}
                      <span className="party-field-hint">Unique username for client portal login</span>
                    </div>

                    <div className="party-field-group">
                      <label className="party-field-label">
                        {isEditMode ? 'Reset Portal Password (Optional)' : 'Initial Portal Password'} <span className="req">*</span>
                      </label>
                      <div className="party-password-wrap">
                        <input
                          type={showPortalPassword ? 'text' : 'password'}
                          className={`party-field-input ${errors.portalPassword ? 'has-error' : ''}`}
                          placeholder={isEditMode ? 'Leave blank to keep existing password' : 'Min 6 characters, e.g. ClientPass123!'}
                          value={formData.portalPassword}
                          onChange={(e) => setFormData({ ...formData, portalPassword: e.target.value })}
                        />
                        <button
                          type="button"
                          className="party-password-toggle"
                          onClick={() => setShowPortalPassword(!showPortalPassword)}
                          title={showPortalPassword ? 'Hide password' : 'Show password'}
                        >
                          {showPortalPassword ? '👁️' : '🔒'}
                        </button>
                      </div>
                      {errors.portalPassword && <span className="party-field-error">{errors.portalPassword}</span>}
                      <span className="party-field-hint">Secure password given to client's administrator</span>
                    </div>

                    <div className="party-field-group">
                      <label className="party-field-label">Portal Authority Role</label>
                      <select
                        className="party-field-input"
                        value={formData.portalRole}
                        onChange={(e) => setFormData({ ...formData, portalRole: e.target.value })}
                      >
                        <option value="ADMIN">ADMIN — Full Access (Orders, Reports, Invoices &amp; Ledger)</option>
                        <option value="BILLING">BILLING — Financial Access (Invoices &amp; Statements Only)</option>
                        <option value="VIEWER">VIEWER — Clinical Access (Orders &amp; Reports Only)</option>
                      </select>
                      <span className="party-field-hint">Granular role-based scope within the customer portal</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Form Actions */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginBottom: '2rem' }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setViewMode('list')}
              disabled={saving}
              style={{ padding: '0.65rem 1.25rem', borderRadius: '8px' }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving}
              style={{ padding: '0.65rem 1.5rem', borderRadius: '8px', fontWeight: 600 }}
            >
              {saving ? 'Saving Party...' : isEditMode ? 'Update B2B Client & Credentials' : 'Save B2B Client & Create Portal Login'}
            </button>
          </div>
        </form>
      )}

      {/* Quick Portal Credential Modal from Table Action */}
      {credModalTarget && (
        <div className="party-cred-modal-overlay" onClick={() => setCredModalTarget(null)}>
          <div className="party-cred-modal" onClick={(e) => e.stopPropagation()}>
            <div className="party-cred-modal-header">
              <h3 className="party-cred-modal-title">🔑 Manage Client Portal Login</h3>
              <button className="party-cred-modal-close" onClick={() => setCredModalTarget(null)}>×</button>
            </div>
            <div className="party-cred-modal-body">
              <div style={{ marginBottom: '1.25rem' }}>
                <div style={{ fontWeight: 700, fontSize: '1rem', color: '#0f172a' }}>{credModalTarget.partyName}</div>
                <div style={{ fontSize: '0.8rem', color: '#64748b' }}>Code: {credModalTarget.partyCode} | Portal Status: {credModalTarget.hasPortalAccess ? 'Active' : 'Unconfigured'}</div>
              </div>

              <div className="party-field-group" style={{ marginBottom: '1rem' }}>
                <label className="party-field-label">Portal Username <span className="req">*</span></label>
                <input
                  type="text"
                  className="party-field-input"
                  value={modalUsername}
                  onChange={(e) => setModalUsername(e.target.value.toLowerCase().trim())}
                  placeholder="e.g. client_admin"
                />
              </div>

              <div className="party-field-group" style={{ marginBottom: '1rem' }}>
                <label className="party-field-label">New / Reset Password</label>
                <div className="party-password-wrap">
                  <input
                    type={modalShowPassword ? 'text' : 'password'}
                    className="party-field-input"
                    value={modalPassword}
                    onChange={(e) => setModalPassword(e.target.value)}
                    placeholder="Enter new password (min 6 chars)"
                  />
                  <button
                    type="button"
                    className="party-password-toggle"
                    onClick={() => setModalShowPassword(!modalShowPassword)}
                  >
                    {modalShowPassword ? '👁️' : '🔒'}
                  </button>
                </div>
                <span className="party-field-hint">Enter a new password to reset client's portal access</span>
              </div>

              <div className="party-field-group">
                <label className="party-field-label">Portal Role</label>
                <select
                  className="party-field-input"
                  value={modalRole}
                  onChange={(e) => setModalRole(e.target.value)}
                >
                  <option value="ADMIN">ADMIN (Full Access)</option>
                  <option value="BILLING">BILLING (Invoices &amp; Ledger)</option>
                  <option value="VIEWER">VIEWER (Reports &amp; Orders)</option>
                </select>
              </div>
            </div>
            <div className="party-cred-modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setCredModalTarget(null)}
                disabled={savingCreds}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSaveModalCreds}
                disabled={savingCreds}
              >
                {savingCreds ? 'Saving Credentials...' : 'Save Portal Credentials'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Financial Ledger (Khata) Modal */}
      {ledgerModalTarget && (
        <div className="party-cred-modal-overlay" onClick={() => setLedgerModalTarget(null)}>
          <div className="party-ledger-modal" onClick={(e) => e.stopPropagation()}>
            <div className="party-cred-modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '1.4rem' }}>📖</span>
                <div>
                  <h3 className="party-cred-modal-title">Financial Ledger &amp; Khata Statement</h3>
                  <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                    {ledgerModalTarget.partyName} ({ledgerModalTarget.partyCode}) &bull; Policy: <strong>{ledgerData?.creditPolicy || ledgerModalTarget.creditPolicy || 'HYBRID'}</strong>
                  </div>
                </div>
              </div>
              <button
                type="button"
                className="party-cred-modal-close"
                onClick={() => setLedgerModalTarget(null)}
              >
                &times;
              </button>
            </div>

            <div className="party-ledger-modal-body">
              {/* Financial KPI Cards */}
              <div className="ledger-kpi-grid">
                <div className="ledger-kpi-card">
                  <div className="ledger-kpi-title">Approved Credit Limit</div>
                  <div className="ledger-kpi-val">₹{parseFloat(ledgerData?.creditLimit || ledgerModalTarget.creditLimit || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
                </div>
                <div className="ledger-kpi-card">
                  <div className="ledger-kpi-title">Total Invoiced (Debits)</div>
                  <div className="ledger-kpi-val danger">₹{parseFloat(ledgerData?.invoicedReceivables || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
                </div>
                <div className="ledger-kpi-card">
                  <div className="ledger-kpi-title">Unapplied Advances / Paid</div>
                  <div className="ledger-kpi-val positive">₹{parseFloat(ledgerData?.unappliedAdvances || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
                </div>
                <div className="ledger-kpi-card">
                  <div className="ledger-kpi-title">Total Net Exposure</div>
                  <div className="ledger-kpi-val warning">₹{parseFloat(ledgerData?.totalExposure || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
                </div>
                <div className="ledger-kpi-card">
                  <div className="ledger-kpi-title">Available Booking Headroom</div>
                  <div className={`ledger-kpi-val ${(ledgerData?.availableCredit || 0) > 0 ? 'positive' : 'danger'}`}>
                    ₹{parseFloat(ledgerData?.availableCredit || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </div>
                </div>
              </div>

              {/* Date Filters */}
              <div className="ledger-filter-row">
                <div className="ledger-filter-item">
                  <label>Statement From</label>
                  <input
                    type="date"
                    value={ledgerFromDate}
                    onChange={(e) => setLedgerFromDate(e.target.value)}
                  />
                </div>
                <div className="ledger-filter-item">
                  <label>Statement To</label>
                  <input
                    type="date"
                    value={ledgerToDate}
                    onChange={(e) => setLedgerToDate(e.target.value)}
                  />
                </div>
                <button
                  type="button"
                  className="btn btn-secondary"
                  style={{ padding: '6px 14px', fontSize: '0.85rem' }}
                  onClick={handleFilterLedger}
                  disabled={ledgerLoading}
                >
                  {ledgerLoading ? 'Loading...' : 'Filter Date Range'}
                </button>
              </div>

              {/* Transactions Table */}
              <div className="ledger-table-wrap">
                {ledgerLoading ? (
                  <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>Loading ledger statement...</div>
                ) : !ledgerData?.entries || ledgerData.entries.length === 0 ? (
                  <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
                    No financial debits or credit payments recorded for this party in the selected date range.
                  </div>
                ) : (
                  <table className="ledger-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Type</th>
                        <th>Ref #</th>
                        <th>Particulars / Notes</th>
                        <th style={{ textAlign: 'right' }}>Debit (₹)</th>
                        <th style={{ textAlign: 'right' }}>Credit (₹)</th>
                        <th style={{ textAlign: 'right' }}>Balance Due (₹)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ledgerData.entries.map((entry, idx) => (
                        <tr key={idx}>
                          <td>{new Date(entry.transactionDate).toLocaleDateString('en-IN')}</td>
                          <td>
                            <span style={{
                              fontWeight: 700,
                              fontSize: '0.75rem',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              background: entry.entryType === 'INVOICE' ? '#fef2f2' : '#f0fdf4',
                              color: entry.entryType === 'INVOICE' ? '#dc2626' : '#16a34a'
                            }}>
                              {entry.entryType}
                            </span>
                          </td>
                          <td style={{ fontFamily: 'monospace', fontWeight: 600 }}>{entry.referenceNumber}</td>
                          <td style={{ color: '#475569' }}>{entry.notes || '—'}</td>
                          <td style={{ textAlign: 'right', fontWeight: 600, color: '#dc2626' }}>
                            {entry.debitAmount > 0 ? `₹${parseFloat(entry.debitAmount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '—'}
                          </td>
                          <td style={{ textAlign: 'right', fontWeight: 600, color: '#16a34a' }}>
                            {entry.creditAmount > 0 ? `₹${parseFloat(entry.creditAmount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '—'}
                          </td>
                          <td style={{ textAlign: 'right', fontWeight: 700, color: '#0f172a' }}>
                            ₹{parseFloat(entry.runningBalance || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            <div className="party-cred-modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => window.print()}
              >
                🖨️ Print Khata Statement
              </button>
              <button
                type="button"
                className="btn btn-success"
                style={{ background: '#10b981', borderColor: '#059669', color: '#ffffff', fontWeight: 600 }}
                onClick={() => handleOpenAdvanceModal(ledgerModalTarget)}
              >
                ⚡ + Deposit Advance / Recharge Quota
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setLedgerModalTarget(null)}
              >
                Close Statement
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Advance Deposit / Wallet Recharge Modal */}
      {advanceModalTarget && (
        <div className="party-cred-modal-overlay" onClick={() => !savingAdvance && setAdvanceModalTarget(null)}>
          <div className="party-cred-modal" style={{ maxWidth: '560px' }} onClick={(e) => e.stopPropagation()}>
            <div className="party-cred-modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '1.4rem' }}>💳</span>
                <div>
                  <h3 className="party-cred-modal-title">Record Advance Deposit / Wallet Recharge</h3>
                  <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                    {advanceModalTarget.partyName} ({advanceModalTarget.partyCode})
                  </div>
                </div>
              </div>
              <button
                type="button"
                className="party-cred-modal-close"
                onClick={() => !savingAdvance && setAdvanceModalTarget(null)}
                disabled={savingAdvance}
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleCreateAdvance}>
              <div className="party-cred-modal-body">
                {/* Current Exposure Summary Banner */}
                <div style={{
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  padding: '12px 14px',
                  marginBottom: '16px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600 }}>CURRENT AVAILABLE HEADROOM</div>
                    <div style={{ fontSize: '1.2rem', fontWeight: 800, color: (advanceModalTarget.availableCredit || 0) > 0 ? '#16a34a' : '#dc2626' }}>
                      ₹{parseFloat(advanceModalTarget.availableCredit || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600 }}>POLICY</div>
                    <span className={`badge-policy ${(advanceModalTarget.creditPolicy || 'PREPAID').toLowerCase()}`}>
                      {advanceModalTarget.creditPolicy || 'PREPAID'}
                    </span>
                  </div>
                </div>

                {/* Amount with quick chips */}
                <div className="party-field-group">
                  <label className="party-field-label">Deposit Amount (₹) <span className="req">*</span></label>
                  <input
                    type="number"
                    min="1"
                    step="0.01"
                    className="party-field-input"
                    value={advanceAmount}
                    onChange={(e) => setAdvanceAmount(e.target.value)}
                    required
                    style={{ fontSize: '1.15rem', fontWeight: 700, color: '#0f172a' }}
                  />
                  <div style={{ display: 'flex', gap: '6px', marginTop: '6px' }}>
                    {[5000, 10000, 20000, 50000].map(slab => (
                      <button
                        key={slab}
                        type="button"
                        className="btn btn-secondary"
                        style={{
                          padding: '3px 8px',
                          fontSize: '0.75rem',
                          background: parseFloat(advanceAmount) === slab ? '#eff6ff' : '#ffffff',
                          borderColor: parseFloat(advanceAmount) === slab ? '#3b82f6' : '#cbd5e1',
                          color: parseFloat(advanceAmount) === slab ? '#1d4ed8' : '#475569',
                          fontWeight: 600
                        }}
                        onClick={() => setAdvanceAmount(slab)}
                      >
                        +₹{slab.toLocaleString('en-IN')}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="party-grid-2" style={{ marginTop: '12px' }}>
                  <div className="party-field-group">
                    <label className="party-field-label">Payment Mode <span className="req">*</span></label>
                    <select
                      className="party-field-input"
                      value={advanceMode}
                      onChange={(e) => setAdvanceMode(e.target.value)}
                    >
                      <option value="UPI">UPI (Google Pay / PhonePe / Paytm)</option>
                      <option value="NEFT">NEFT / RTGS Bank Transfer</option>
                      <option value="IMPS">IMPS Instant Transfer</option>
                      <option value="CHEQUE">Cheque / Demand Draft</option>
                      <option value="CASH">Cash Deposit</option>
                      <option value="CARD">Debit / Credit Card</option>
                    </select>
                  </div>

                  <div className="party-field-group">
                    <label className="party-field-label">Transaction Ref / UTR / Cheque #</label>
                    <input
                      type="text"
                      className="party-field-input"
                      placeholder="e.g. UTR2026101089324"
                      value={advanceRef}
                      onChange={(e) => setAdvanceRef(e.target.value)}
                    />
                  </div>
                </div>

                <div className="party-grid-2" style={{ marginTop: '12px' }}>
                  <div className="party-field-group">
                    <label className="party-field-label">Drawee Bank Name (Optional)</label>
                    <input
                      type="text"
                      className="party-field-input"
                      placeholder="e.g. HDFC Bank, ICICI Bank"
                      value={advanceBank}
                      onChange={(e) => setAdvanceBank(e.target.value)}
                    />
                  </div>

                  <div className="party-field-group">
                    <label className="party-field-label">Accounting Notes / Remarks</label>
                    <input
                      type="text"
                      className="party-field-input"
                      value={advanceNotes}
                      onChange={(e) => setAdvanceNotes(e.target.value)}
                    />
                  </div>
                </div>

                {/* Instant Quota Simulation */}
                <div style={{
                  marginTop: '16px',
                  background: '#f0fdf4',
                  border: '1px solid #bbf7d0',
                  borderRadius: '8px',
                  padding: '10px 14px',
                  fontSize: '0.82rem',
                  color: '#166534'
                }}>
                  ✨ <strong>Quota Impact:</strong> Upon posting, an official advance receipt (<code>ADV/YYYY/XXXXXX</code>) will be generated. The party's available booking headroom will immediately increase by <strong>₹{parseFloat(advanceAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</strong>, instantly unblocking sample accession!
                </div>
              </div>

              <div className="party-cred-modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setAdvanceModalTarget(null)}
                  disabled={savingAdvance}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  style={{ background: '#10b981', borderColor: '#059669' }}
                  disabled={savingAdvance}
                >
                  {savingAdvance ? 'Recording Advance...' : `Credit ₹${parseFloat(advanceAmount || 0).toLocaleString('en-IN')} to Wallet`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirm Modal */}
      {confirmTarget && (
        <ConfirmModal
          isOpen={true}
          title={`${confirmTarget.nextStatus ? 'Activate' : 'Deactivate'} Party`}
          message={`Are you sure you want to ${confirmTarget.nextStatus ? 'activate' : 'deactivate'} client "${confirmTarget.partyName}"?`}
          confirmLabel={confirmTarget.nextStatus ? 'Activate' : 'Deactivate'}
          confirmVariant={confirmTarget.nextStatus ? 'primary' : 'danger'}
          onConfirm={confirmStatusChange}
          onCancel={() => setConfirmTarget(null)}
        />
      )}
    </MasterPageWrapper>
  );
};

export default PartyMasterPage;
